import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DeleteOutlined, PlusOutlined, QuestionCircleFilled } from "@ant-design/icons";
import { Form, Pagination, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { FormServiceFieldTechnicalDefinitionsCookieDuration, FormServiceFieldTechnicalDefinitionsCookieHost, FormServiceFieldTechnicalDefinitionsCookieName, FormServiceFieldTechnicalDefinitionsCookiePurpose, FormServiceFieldTechnicalDefinitionsCookieType, TECHNICAL_DEFINITION_DEFAULTS } from "..";
import { useI18n } from "../../../../../../contexts";
import { useDndSortable } from "../../../../../../hooks";
import { FormValueDifferFromTemplateTag } from "../../../..";
import { useRunCallbackNextRender } from "@devowl-wp/react-utils";
const FormServiceFieldTechnicalDefinitionsLayout = {
    labelCol: {
        span: 0
    },
    wrapperCol: {
        span: 24
    },
    style: {
        margin: 0
    }
};
const DiffTable = (param)=>{
    let { technicalDefinitions } = param;
    const [form] = Form.useForm();
    useEffect(()=>{
        form.setFieldsValue({
            technicalDefinitions
        });
    }, [
        technicalDefinitions
    ]);
    return /*#__PURE__*/ _jsx(Form, {
        form: form,
        initialValues: {
            isEmbeddingOnlyExternalResources: false,
            technicalDefinitions
        },
        children: /*#__PURE__*/ _jsx(FormServiceFieldTechnicalDefinitionsTable, {
            isDiff: true
        })
    });
};
const diffCalculate = (oldValue, newValue)=>{
    // Compose entries by all available keys for "modified" calculation
    const numberedIndexedForPosition = [];
    const oldEntriesModified = oldValue.reduce((a, param)=>{
        let { name, host, duration, durationUnit, isSessionDuration, type, purpose } = param;
        const idx = `${type}/${name}/${host}`;
        a[idx] = `${duration}/${durationUnit}/${isSessionDuration}/${type}/${purpose || ""}`;
        numberedIndexedForPosition.push(idx);
        return a;
    }, {});
    const modified = newValue.map((definition)=>{
        const { name, host, duration, durationUnit, isSessionDuration, type, purpose } = definition;
        const idxName = `${type}/${name}/${host}`;
        const oldModified = oldEntriesModified[idxName];
        return typeof oldModified === "undefined" || oldModified === `${duration}/${durationUnit}/${isSessionDuration}/${type}/${purpose || ""}` ? undefined : {
            ...definition,
            position: numberedIndexedForPosition.indexOf(idxName)
        };
    }).filter(Boolean);
    // Compose entries by name and host for "addition" calculation
    const oldEntriesComposedAdded = oldValue.map((param)=>{
        let { type, name, host } = param;
        return `${type}/${name}/${host}`;
    });
    const added = newValue.map((definition, position)=>{
        const { type, name, host } = definition;
        return !oldEntriesComposedAdded.includes(`${type}/${name}/${host}`) ? {
            ...definition,
            position
        } : undefined;
    }).filter(Boolean);
    return added.length || modified.length ? {
        added,
        modified
    } : undefined;
};
const FormServiceFieldTechnicalDefinitionsTable = (param)=>{
    let { isDiff = false } = param;
    const { __, _i } = useI18n();
    const { SortableContext, SortableRow, DragHandle } = useDndSortable();
    const runCallbackNextRender = useRunCallbackNextRender();
    const [currentPagination, setCurrentPagination] = useState({
        current: 1,
        pageSize: 10
    });
    return /*#__PURE__*/ _jsx(Form.Item, {
        noStyle: true,
        shouldUpdate: (prevValues, nextValues)=>prevValues.isEmbeddingOnlyExternalResources !== nextValues.isEmbeddingOnlyExternalResources || (isDiff ? JSON.stringify(prevValues.technicalDefinitions) !== JSON.stringify(nextValues.technicalDefinitions) : prevValues.technicalDefinitions.length !== nextValues.technicalDefinitions.length),
        children: (param)=>{
            let { getFieldValue, validateFields, getFieldsValue } = param;
            return getFieldValue("isEmbeddingOnlyExternalResources") ? null : /*#__PURE__*/ _jsx(Form.List, {
                name: "technicalDefinitions",
                children: (fields, param)=>{
                    let { add, remove, move } = param;
                    const { current, pageSize } = currentPagination;
                    const minPageItem = (current - 1) * pageSize; // => (1 - 1) * 10 = 0
                    const maxPageItem = current * pageSize; // 1 * 10 = 10
                    const lastPage = Math.ceil(fields.length / pageSize);
                    return /*#__PURE__*/ _jsxs("table", {
                        className: "wp-list-table widefat fixed striped table-view-list",
                        style: {
                            marginBottom: 25
                        },
                        children: [
                            /*#__PURE__*/ _jsx("thead", {
                                children: /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            width: 45,
                                            align: "right",
                                            children: "\xa0"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            width: 150,
                                            children: __("Cookie type")
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: /*#__PURE__*/ _jsx(Tooltip, {
                                                title: _i(__("Every cookie has a technical name, which you must provide. If a cookie name is dynamically composed, please use an asterisk ({{code}}*{{/code}}) as a wildcard (placeholder)."), {
                                                    code: /*#__PURE__*/ _jsx("code", {})
                                                }),
                                                children: /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        __("Technical cookie name"),
                                                        " ",
                                                        /*#__PURE__*/ _jsx(QuestionCircleFilled, {})
                                                    ]
                                                })
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: /*#__PURE__*/ _jsx(Tooltip, {
                                                title: __("Every cookie is associated to a domain or hostname."),
                                                children: /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        __("Technical cookie host"),
                                                        " ",
                                                        /*#__PURE__*/ _jsx(QuestionCircleFilled, {})
                                                    ]
                                                })
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            width: 290,
                                            children: /*#__PURE__*/ _jsx(Tooltip, {
                                                title: __("A HTTP cookie is only valid for a certain time, which is defined when the cookie is set."),
                                                children: /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        __("Cookie duration"),
                                                        " ",
                                                        /*#__PURE__*/ _jsx(QuestionCircleFilled, {})
                                                    ]
                                                })
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: /*#__PURE__*/ _jsx(Tooltip, {
                                                title: __("Each cookie serves a purpose (e.g. user identification for tracking), which should be explained."),
                                                children: /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        __("Purpose"),
                                                        " ",
                                                        /*#__PURE__*/ _jsx(QuestionCircleFilled, {})
                                                    ]
                                                })
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            width: 70,
                                            align: "right",
                                            children: "\xa0"
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx("tbody", {
                                children: /*#__PURE__*/ _jsx(SortableContext, {
                                    items: fields.map((param)=>{
                                        let { key } = param;
                                        return `${key}`;
                                    }),
                                    onDragEnd: (param)=>{
                                        let { active, over } = param;
                                        const from = fields.findIndex((field)=>field.key === +active.id);
                                        const to = fields.findIndex((field)=>field.key === +(over == null ? void 0 : over.id));
                                        move(from, to);
                                    },
                                    children: fields.map((field, index)=>{
                                        const currentPageItem = index + 1; // => 1
                                        const isInPage = currentPageItem > minPageItem && currentPageItem <= maxPageItem;
                                        return /*#__PURE__*/ _jsxs(SortableRow, {
                                            "data-row-key": `${field.key}`,
                                            style: {
                                                display: isInPage ? undefined : "none"
                                            },
                                            children: [
                                                /*#__PURE__*/ _jsx("td", {
                                                    children: fields.length > 1 && !isDiff ? /*#__PURE__*/ _jsx(DragHandle, {}) : undefined
                                                }),
                                                /*#__PURE__*/ _jsx("td", {
                                                    children: /*#__PURE__*/ _jsx(FormServiceFieldTechnicalDefinitionsCookieType, {
                                                        field: field,
                                                        readOnly: isDiff
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("td", {
                                                    children: /*#__PURE__*/ _jsx(FormServiceFieldTechnicalDefinitionsCookieName, {
                                                        field: field,
                                                        readOnly: isDiff
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("td", {
                                                    children: /*#__PURE__*/ _jsx(FormServiceFieldTechnicalDefinitionsCookieHost, {
                                                        field: field,
                                                        readOnly: isDiff
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("td", {
                                                    children: /*#__PURE__*/ _jsx(FormServiceFieldTechnicalDefinitionsCookieDuration, {
                                                        field: field,
                                                        readOnly: isDiff
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("td", {
                                                    children: /*#__PURE__*/ _jsx(FormServiceFieldTechnicalDefinitionsCookiePurpose, {
                                                        field: field,
                                                        readOnly: isDiff
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("td", {
                                                    children: fields.length > 1 && !isDiff ? /*#__PURE__*/ _jsx("a", {
                                                        className: "button button-small",
                                                        onClick: ()=>{
                                                            remove(field.name);
                                                            // Automatically select the previous page when necessery
                                                            if (current > 1 && current === lastPage && fields.length % pageSize === 1) {
                                                                setCurrentPagination({
                                                                    current: current - 1,
                                                                    pageSize
                                                                });
                                                            }
                                                        },
                                                        children: /*#__PURE__*/ _jsx(DeleteOutlined, {})
                                                    }) : null
                                                })
                                            ]
                                        }, field.key);
                                    })
                                })
                            }),
                            !isDiff && /*#__PURE__*/ _jsx("tfoot", {
                                children: /*#__PURE__*/ _jsx("tr", {
                                    children: /*#__PURE__*/ _jsxs("td", {
                                        colSpan: 7,
                                        align: "right",
                                        children: [
                                            /*#__PURE__*/ _jsxs("a", {
                                                className: "button button-primary alignright",
                                                onClick: ()=>{
                                                    add(TECHNICAL_DEFINITION_DEFAULTS);
                                                    // Automatically select the new page when necessery
                                                    const newCurrent = lastPage + (fields.length > 0 && fields.length % pageSize === 0 ? 1 : 0);
                                                    if (current !== newCurrent) {
                                                        setCurrentPagination({
                                                            current: newCurrent,
                                                            pageSize
                                                        });
                                                    }
                                                },
                                                children: [
                                                    /*#__PURE__*/ _jsx(PlusOutlined, {}),
                                                    " ",
                                                    __("Add another cookie definition")
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(Pagination, {
                                                onChange: (page, pageSize)=>{
                                                    const current = currentPagination.pageSize !== pageSize ? 1 : page;
                                                    setCurrentPagination({
                                                        current,
                                                        pageSize
                                                    });
                                                    const minPageItem = (current - 1) * pageSize; // => (1 - 1) * 10 = 0
                                                    const maxPageItem = current * pageSize; // 1 * 10 = 10
                                                    // Antd cannot render the validation errors in a hidden field, so we need to wait for
                                                    // the next page is rendered and validate again those errors
                                                    const fieldsToValidate = getFieldsValue([
                                                        "technicalDefinitions"
                                                    ]).technicalDefinitions.map((td, index)=>{
                                                        const currentPageItem = index + 1; // => 1
                                                        const isInNextPage = currentPageItem > minPageItem && currentPageItem <= maxPageItem;
                                                        return isInNextPage && Object.keys(td).map((key)=>[
                                                                "technicalDefinitions",
                                                                index,
                                                                key
                                                            ]);
                                                    }).filter(Boolean).flat();
                                                    runCallbackNextRender(()=>validateFields(fieldsToValidate));
                                                },
                                                ...currentPagination,
                                                showLessItems: true,
                                                showTotal: (total)=>__("Total %d items", total),
                                                size: "small",
                                                total: fields.length,
                                                className: "alignright",
                                                pageSizeOptions: [
                                                    10,
                                                    20,
                                                    50,
                                                    100,
                                                    1000
                                                ],
                                                style: {
                                                    marginRight: 10
                                                },
                                                showSizeChanger: true
                                            }),
                                            /*#__PURE__*/ _jsx(FormValueDifferFromTemplateTag, {
                                                useModal: true,
                                                form: "service",
                                                valueName: "technicalDefinitions",
                                                noBr: true,
                                                style: {
                                                    marginTop: 4
                                                },
                                                difference: diffCalculate,
                                                apply: (param, setFieldsValue, oldValue)=>{
                                                    let { added, modified } = param;
                                                    let technicalDefinitions = oldValue;
                                                    modified.forEach((param)=>{
                                                        let { position, ...rest } = param;
                                                        technicalDefinitions[position] = rest;
                                                    });
                                                    added.forEach((param)=>{
                                                        let { position, ...rest } = param;
                                                        technicalDefinitions.splice(position, 0, rest);
                                                    });
                                                    // Remove empty entries (caused by switching from `isEmbeddingOnlyExternalResources=true` to `false`)
                                                    technicalDefinitions = technicalDefinitions.filter((param)=>{
                                                        let { host, name, duration } = param;
                                                        return host !== "" && name !== "" && duration !== undefined;
                                                    });
                                                    // Remove duplicates (from bottom to top)
                                                    technicalDefinitions.forEach((a)=>{
                                                        const { type: typeA, host: hostA, name: nameA, _duplicate } = a;
                                                        if (!_duplicate) {
                                                            const duplicate = technicalDefinitions.find((b)=>{
                                                                const { type: typeB, host: hostB, name: nameB } = b;
                                                                return a !== b && typeA === typeB && hostA === hostB && nameA === nameB;
                                                            });
                                                            if (duplicate) {
                                                                duplicate._duplicate = true;
                                                            }
                                                        }
                                                    });
                                                    technicalDefinitions = technicalDefinitions.filter((param)=>{
                                                        let { _duplicate } = param;
                                                        return !_duplicate;
                                                    });
                                                    setFieldsValue({
                                                        technicalDefinitions
                                                    });
                                                },
                                                newValueText: false,
                                                renderDiff: (value, param)=>{
                                                    let { added, modified } = param;
                                                    return /*#__PURE__*/ _jsxs(_Fragment, {
                                                        children: [
                                                            added.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                                                                children: [
                                                                    /*#__PURE__*/ _jsx("p", {
                                                                        children: /*#__PURE__*/ _jsx("strong", {
                                                                            children: __("Missing entries:")
                                                                        })
                                                                    }),
                                                                    /*#__PURE__*/ _jsx(DiffTable, {
                                                                        technicalDefinitions: added
                                                                    })
                                                                ]
                                                            }),
                                                            modified.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                                                                children: [
                                                                    /*#__PURE__*/ _jsx("p", {
                                                                        children: /*#__PURE__*/ _jsx("strong", {
                                                                            children: __("Modified entries:")
                                                                        })
                                                                    }),
                                                                    /*#__PURE__*/ _jsx(DiffTable, {
                                                                        technicalDefinitions: modified
                                                                    })
                                                                ]
                                                            })
                                                        ]
                                                    });
                                                }
                                            })
                                        ]
                                    })
                                })
                            })
                        ]
                    });
                }
            });
        }
    });
};
export { FormServiceFieldTechnicalDefinitionsLayout, FormServiceFieldTechnicalDefinitionsTable };
